import { Icon } from '@toggle/design-system';
import { formatPercentage } from '@toggle/helpers';
import { AssetClassCode, ExploreInsightDirection } from '@toggle/toggle';
import { t } from 'i18next';
import React from 'react';

import { useEntity } from '~/hooks/use-entities';

import * as S from './InsightDetails.styles';

const SUFFIX_PERCENT = '%';
const DECIMALS_PERCENT = 2;
const SUFFIX_BPS = 'bps';
const DECIMALS_BPS = 0;

export interface InsightDetailsProps {
  horizon: string;
  stars: number;
  isNewInsight?: boolean;
  median: number;
  direction: ExploreInsightDirection;
  name: string;
  isTooltip?: boolean;
  entityTag?: string;
  episodeLabel: string;
}

export const InsightDetails = ({
  name,
  stars,
  horizon,
  median,
  direction,
  isNewInsight,
  isTooltip = false,
  entityTag,
  episodeLabel,
}: InsightDetailsProps) => {
  const { data } = useEntity(entityTag ?? '');

  const isFixedIncome = data?.data?.asset_class === AssetClassCode.Fi;
  const isBullish = direction === ExploreInsightDirection.Bullish;

  const suffix = isFixedIncome ? SUFFIX_BPS : SUFFIX_PERCENT;
  const decimals = isFixedIncome ? DECIMALS_BPS : DECIMALS_PERCENT;
  const formattedMedian = formatPercentage(median, {
    suffix,
    decimals,
  });

  return (
    <S.MainInfo $isTooltip={isTooltip}>
      <S.InsightNameWrapper>
        <S.InsightName $isTooltip={isTooltip}>{name}</S.InsightName>
        {isNewInsight && (
          <S.StyledBadge
            label={t('insights:insightCard.new')}
            data-testid="is-new-badge"
          />
        )}
      </S.InsightNameWrapper>
      <S.InfoDetailWrapper>
        <S.InfoDetail $isTooltip={isTooltip}>
          <S.Median $isBullish={isBullish}>{formattedMedian}</S.Median>
          <S.Dot>&middot;</S.Dot>
          <span>{horizon.toUpperCase()}</span>
          <S.Dot>&middot;</S.Dot>
          <S.StarsLabel aria-label={`Rating: ${stars} stars`}>
            <Icon iconName="Rating" size={10} aria-hidden="true" />
            <span aria-current="true">{stars}</span>
          </S.StarsLabel>
        </S.InfoDetail>
        <S.Episode>{episodeLabel}</S.Episode>
      </S.InfoDetailWrapper>
    </S.MainInfo>
  );
};

InsightDetails.Styled = S;
